import { Typography, cn } from "@eisox/design-system";
import { ChevronDownIcon, ChevronRightIcon } from "@eisox/icons";
import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu
 * -----------------------------------------------------------------------------------------------*/

const DROPDOWN_MENU_NAME = "DropdownMenu";

type DropdownMenuProps = RadixDropdownMenu.DropdownMenuProps;

const Root = RadixDropdownMenu.Root;

Root.displayName = DROPDOWN_MENU_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = RadixDropdownMenu.DropdownMenuTriggerProps;

const Trigger: React.FC<TriggerProps> = ({ onBlur, className, children, ...props }) => (
  <RadixDropdownMenu.Trigger
    {...props}
    className={cn(
      "font-light w-60 rounded-xl px-5 [&_svg]:ml-2.5 py-2.5 flex cursor-pointer items-center justify-between border border-primary-darker-gray bg-primary-white text-base italic disabled:cursor-default disabled:bg-primary-gray [&_svg]:shrink-0",
      className,
    )}
    onBlur={(event: React.FocusEvent<HTMLButtonElement>) => {
      const element = event.relatedTarget;
      (element as HTMLElement).focus();
      onBlur?.(event);
    }}
  >
    <Typography>{children}</Typography>
    <ChevronDownIcon />
  </RadixDropdownMenu.Trigger>
);

Trigger.displayName = DROPDOWN_MENU_NAME + "Trigger";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = RadixDropdownMenu.DropdownMenuContentProps;

const Content: React.FC<ContentProps> = ({
  align = "start",
  sideOffset = 5,
  collisionPadding = { top: 100, bottom: 100, right: 20, left: 20 },
  className,
  children,
  ...props
}) => (
  <RadixDropdownMenu.Portal>
    <RadixDropdownMenu.Content
      {...props}
      className={cn(
        // oxlint-disable-next-line tailwindcss/no-hardcoded-colors
        "p-1.25 z-1400 h-fit max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) overflow-auto rounded-[10px] bg-primary-white shadow-[0px_4px_20px_0px_#12213833]",
        className,
      )}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
    >
      {children}
    </RadixDropdownMenu.Content>
  </RadixDropdownMenu.Portal>
);

Content.displayName = DROPDOWN_MENU_NAME + "Content";

/* -------------------------------------------------------------------------------------------------
 * Sub
 * -----------------------------------------------------------------------------------------------*/

type SubProps = RadixDropdownMenu.DropdownMenuSubProps;

const Sub = RadixDropdownMenu.Sub;

Sub.displayName = DROPDOWN_MENU_NAME + "Sub";

/* -------------------------------------------------------------------------------------------------
 * SubTrigger
 * -----------------------------------------------------------------------------------------------*/

type SubTriggerProps = RadixDropdownMenu.DropdownMenuSubTriggerProps;

const SubTrigger: React.FC<SubTriggerProps> = ({ className, children, ...props }) => (
  <RadixDropdownMenu.SubTrigger
    {...props}
    className={cn(
      "gap-x-2.5 p-1.25 flex items-center justify-between rounded-[10px] text-base data-disabled:text-primary-darker-gray data-highlighted:cursor-pointer data-highlighted:bg-primary-gray data-highlighted:outline-none data-[state=open]:bg-primary-gray",
      className,
    )}
  >
    {children}
    <ChevronRightIcon />
  </RadixDropdownMenu.SubTrigger>
);

SubTrigger.displayName = DROPDOWN_MENU_NAME + "SubTrigger";

/* -------------------------------------------------------------------------------------------------
 * SubContent
 * -----------------------------------------------------------------------------------------------*/

type SubContentProps = RadixDropdownMenu.DropdownMenuSubContentProps;

const SubContent: React.FC<SubContentProps> = ({
  sideOffset = 12,
  alignOffset = -10,
  collisionPadding = { top: 100, bottom: 100, right: 20, left: 20 },
  className,
  children,
  ...props
}) => (
  <RadixDropdownMenu.SubContent
    {...props}
    className={cn(
      // oxlint-disable-next-line tailwindcss/no-hardcoded-colors
      "p-1.25 z-1400 h-fit max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) overflow-auto rounded-[10px] bg-primary-white shadow-[0px_4px_20px_0px_#12213833]",
      className,
    )}
    sideOffset={sideOffset}
    alignOffset={alignOffset}
    collisionPadding={collisionPadding}
  >
    {children}
  </RadixDropdownMenu.SubContent>
);

SubContent.displayName = DROPDOWN_MENU_NAME + "SubContent";

/* -------------------------------------------------------------------------------------------------
 * CheckboxItem
 * -----------------------------------------------------------------------------------------------*/

type CheckboxItemProps = RadixDropdownMenu.DropdownMenuCheckboxItemProps;

const CheckboxItem: React.FC<CheckboxItemProps> = ({ onSelect, className, children, ...props }) => (
  <RadixDropdownMenu.CheckboxItem
    {...props}
    className={cn(
      "gap-x-2.5 p-1.25 flex items-center justify-start rounded-[10px] text-base data-disabled:text-primary-darker-gray data-highlighted:cursor-pointer data-highlighted:bg-primary-gray data-highlighted:outline-none data-[state=open]:bg-primary-gray",
      className,
    )}
    onSelect={(e: Event) => {
      e.preventDefault();
      onSelect?.(e);
    }}
  >
    {children}
  </RadixDropdownMenu.CheckboxItem>
);

CheckboxItem.displayName = DROPDOWN_MENU_NAME + "CheckboxItem";

export { CheckboxItem, Content, Root, Sub, SubContent, SubTrigger, Trigger };
export type {
  CheckboxItemProps,
  ContentProps,
  DropdownMenuProps,
  SubContentProps,
  SubProps,
  SubTriggerProps,
  TriggerProps,
};
